<template>
  <transition name="filter-employee-modal" appear>
    <div
      v-if="value"
      class="filter-employee-modal__overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="filter-employee-modal-title"
      @mousedown.self="handleClose"
    >
      <div class="filter-employee-modal__card">
        <div class="filter-employee-modal__header">
          <span id="filter-employee-modal-title" class="filter-employee-modal__title">
            Filter employees
          </span>
          <button
            type="button"
            class="filter-employee-modal__close-btn"
            aria-label="Close"
            @click="handleClose"
          >
            <Icon name="popup_close" />
          </button>
        </div>

        <div class="filter-employee-modal__filters-row">
          <div class="filter-employee-modal__column">
            <span class="filter-employee-modal__column-title">Status</span>
            <div class="filter-employee-modal__options">
              <CheckboxInput
                v-for="option in statusOptions"
                :key="option.value"
                :label="option.label"
                :value="statuses[option.value]"
                @input="updateStatus(option.value, $event)"
              />
            </div>
          </div>

          <div class="filter-employee-modal__column filter-employee-modal__column--radio">
            <RadioInput
              label="Archived"
              :availableItems="archivedOptions"
              :checkedItem="includeArchivedLabel"
              :uncheckedValue="archivedClearKey"
              @changeradio="onArchivedChange"
            />
          </div>

          <!-- ER-10492 / MDP-551: single-select radio filter groups (Filter Modal Standard). -->
          <template v-if="showRadioGroups">
            <div class="filter-employee-modal__column filter-employee-modal__column--radio">
              <RadioInput
                :label="radioGroupDefs.occupies.label"
                :availableItems="radioGroupDefs.occupies.options"
                :checkedItem="occupiesCheckedItem"
                @changeradio="onRadioGroupChange('occupies', $event)"
              />
            </div>
            <div
              v-if="showPreparesRadioGroup"
              class="filter-employee-modal__column filter-employee-modal__column--radio"
            >
              <RadioInput
                :label="radioGroupDefs.prepares.label"
                :availableItems="radioGroupDefs.prepares.options"
                :checkedItem="preparesCheckedItem"
                @changeradio="onRadioGroupChange('prepares', $event)"
              />
            </div>
          </template>
        </div>

        <template v-if="showRadioGroups">
          <div class="filter-employee-modal__divider" />
          <div
            class="filter-employee-modal__filters-row filter-employee-modal__filters-row--secondary"
          >
            <div class="filter-employee-modal__column filter-employee-modal__column--radio">
              <RadioInput
                :label="radioGroupDefs.access.label"
                :availableItems="radioGroupDefs.access.options"
                :checkedItem="accessCheckedItem"
                @changeradio="onRadioGroupChange('access', $event)"
              />
            </div>
            <div class="filter-employee-modal__column filter-employee-modal__column--radio">
              <RadioInput
                :label="radioGroupDefs.profilePicture.label"
                :availableItems="radioGroupDefs.profilePicture.options"
                :checkedItem="profilePictureCheckedItem"
                @changeradio="onRadioGroupChange('profilePicture', $event)"
              />
            </div>
          </div>
        </template>

        <div class="filter-employee-modal__divider" />

        <div class="filter-employee-modal__department">
          <span class="filter-employee-modal__column-title">Department</span>

          <DepartmentPickerField
            browse-list
            :value="department"
            :departments="departments"
            :loading="loading"
            @input="setDepartment"
            @search="onSearch"
            @clear="onClear"
          />

          <p v-if="!department" class="filter-employee-modal__hint">
            Leave empty to show employees across the whole organization.
          </p>

          <template v-else>
            <div
              class="filter-employee-modal__scope-toggle"
              role="radiogroup"
              aria-label="Department scope"
            >
              <button
                type="button"
                class="filter-employee-modal__scope-option"
                :class="{ 'filter-employee-modal__scope-option--active': scope === 'direct' }"
                role="radio"
                :aria-checked="scope === 'direct'"
                @click="setScope('direct')"
              >
                This department only
              </button>
              <span class="filter-employee-modal__scope-divider" aria-hidden="true" />
              <button
                type="button"
                class="filter-employee-modal__scope-option"
                :class="{ 'filter-employee-modal__scope-option--active': scope === 'subtree' }"
                role="radio"
                :aria-checked="scope === 'subtree'"
                @click="setScope('subtree')"
              >
                Include all sub-departments
              </button>
            </div>
            <p class="filter-employee-modal__hint">
              {{ scopeHint }}
            </p>
          </template>
        </div>

        <div class="filter-employee-modal__divider" />

        <div class="filter-employee-modal__footer">
          <button
            type="button"
            class="filter-employee-modal__reset"
            @click="handleReset"
          >
            Reset to defaults
          </button>
          <StandardButton
            type="magenta"
            variant="primary"
            size="small"
            label="Apply"
            @click="handleApply"
          />
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from "vue";
import Icon from "@components/icons/Icon.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import DepartmentPickerField from "@components/DepartmentPickerField/DepartmentPickerField.vue";
import CheckboxInput from "@components/Checkbox/CheckboxInput.vue";
import RadioInput from "@components/Radio/RadioInput.vue";
import {
  DEFAULT_EMPLOYEE_RADIO_FILTER_PAYLOAD,
  EMPLOYEE_RADIO_GROUPS,
  EMPLOYEE_RADIO_LABEL_BY_VALUE,
  getEmployeeRadioFilterValueFromLabel,
} from "./employeeFilterPayload";
import { isProductReadinessEnabled } from "@/utils/accountProductFlags";

const props = defineProps({
  value: { type: Boolean, default: false },
  initialStatuses: {
    type: Object,
    default: () => ({ draft: true, active: true, terminated: false }),
  },
  initialIncludeArchived: { type: Boolean, default: false },
  initialDepartment: { type: Object, default: null },
  initialScope: {
    type: String,
    default: "subtree",
    validator: (value) => ["direct", "subtree"].includes(value),
  },
  departments: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  /** ER-10492 / MDP-551 — show the new single-select radio filter groups. */
  showRadioGroups: { type: Boolean, default: false },
  initialOccupies: { type: String, default: "" },
  initialProfilePicture: { type: String, default: "" },
  initialAccess: { type: String, default: "" },
  initialPrepares: { type: String, default: "" },
});

const DEFAULT_STATUSES = { draft: true, active: true, terminated: false };
const DEFAULT_INCLUDE_ARCHIVED = false;
const DEFAULT_SCOPE = "subtree";

const emit = defineEmits(["input", "close", "apply", "reset", "search", "clear"]);

const statusOptions = [
  { label: "Active", value: "active" },
  { label: "Draft", value: "draft" },
  { label: "Terminated", value: "terminated" },
];

// ER-11658: options carry a stable value alongside the display label. RadioInput itself
// still identifies the selected item by label (its v-radio binds :value="radio.label"),
// so the label shown/emitted is resolved from this array rather than a hardcoded literal.
const archivedOptions = [
  { label: "Do Not Show", value: "no_archived" },
  { label: "Show", value: "show_archived" },
];
const archivedClearKey = ref("");

const NO_FILTER_LABEL = "No Filter";

// Labels and values come from EMPLOYEE_RADIO_GROUPS (single source of truth). Each group
// gets its own copy of every option, including its own "No Filter" sentinel.
const radioGroupDefs = Object.fromEntries(
  EMPLOYEE_RADIO_GROUPS.map((group) => [
    group.key,
    { label: group.label, options: group.items.map((item) => ({ ...item })) },
  ])
);

const statuses = ref({ ...DEFAULT_STATUSES, ...(props.initialStatuses || {}) });
const includeArchived = ref(props.initialIncludeArchived);
const department = ref(props.initialDepartment);
const scope = ref(props.initialScope);

const radioGroups = ref({
  ...DEFAULT_EMPLOYEE_RADIO_FILTER_PAYLOAD,
  occupies: props.initialOccupies,
  profilePicture: props.initialProfilePicture,
  access: props.initialAccess,
  prepares: props.initialPrepares,
});

// ER-11658: resolved from `archivedOptions` by stable value, so the labels live in one
// place (the options array) instead of being hardcoded again here.
const includeArchivedLabel = computed(
  () =>
    archivedOptions.find(
      (option) => option.value === (includeArchived.value ? "show_archived" : "no_archived")
    )?.label
);

const radioCheckedItem = (value) => EMPLOYEE_RADIO_LABEL_BY_VALUE[value] || NO_FILTER_LABEL;
const occupiesCheckedItem = computed(() => radioCheckedItem(radioGroups.value.occupies));
const profilePictureCheckedItem = computed(() =>
  radioCheckedItem(radioGroups.value.profilePicture)
);
const accessCheckedItem = computed(() => radioCheckedItem(radioGroups.value.access));
const preparesCheckedItem = computed(() => radioCheckedItem(radioGroups.value.prepares));

const showPreparesRadioGroup = computed(
  () => props.showRadioGroups && isProductReadinessEnabled()
);

const scopeHint = computed(() =>
  scope.value === "subtree"
    ? "Showing this department plus every department nested beneath it, at any depth."
    : "Showing employees belonging directly to this department only."
);

watch(
  () => props.value,
  (open) => {
    if (open) {
      syncFromProps();
      emit("search", "");
    }
  },
  { immediate: true }
);

function syncFromProps() {
  statuses.value = { ...DEFAULT_STATUSES, ...(props.initialStatuses || {}) };
  includeArchived.value = props.initialIncludeArchived;
  department.value = props.initialDepartment;
  scope.value = props.initialScope;
  radioGroups.value = {
    ...DEFAULT_EMPLOYEE_RADIO_FILTER_PAYLOAD,
    occupies: props.initialOccupies || "",
    profilePicture: props.initialProfilePicture || "",
    access: props.initialAccess || "",
    prepares: props.initialPrepares || "",
  };
  if (!isProductReadinessEnabled()) {
    radioGroups.value = { ...radioGroups.value, prepares: "" };
  }
}

function updateStatus(key, checked) {
  statuses.value = { ...statuses.value, [key]: !!checked };
}

function onArchivedChange(payload) {
  // ER-11658: RadioInput emits the selected item's label (it identifies items by label,
  // not value) — resolve it back to a stable value via the options array rather than
  // hardcoding a label here.
  const option = archivedOptions.find((item) => item.label === payload?.value);
  includeArchived.value = option?.value === "show_archived";
}

function onRadioGroupChange(key, payload) {
  radioGroups.value = {
    ...radioGroups.value,
    [key]: getEmployeeRadioFilterValueFromLabel(payload?.value),
  };
}

function setDepartment(dept) {
  department.value = dept;
  if (!dept) scope.value = DEFAULT_SCOPE;
}

function setScope(val) {
  scope.value = val;
}

function onSearch(query) {
  emit("search", query);
}

function onClear() {
  emit("clear");
  emit("search", "");
}

function handleReset() {
  statuses.value = { ...DEFAULT_STATUSES };
  includeArchived.value = DEFAULT_INCLUDE_ARCHIVED;
  department.value = null;
  scope.value = DEFAULT_SCOPE;
  radioGroups.value = { ...DEFAULT_EMPLOYEE_RADIO_FILTER_PAYLOAD };
  archivedClearKey.value = `reset-${Date.now()}`;
  emit("reset");
  emit("search", "");
}

function handleApply() {
  emit("apply", {
    status: statusOptions
      .filter((option) => statuses.value[option.value])
      .map((option) => option.value),
    includeArchived: includeArchived.value,
    department: department.value,
    scope: department.value ? scope.value : null,
    occupies: radioGroups.value.occupies || "",
    profilePicture: radioGroups.value.profilePicture || "",
    access: radioGroups.value.access || "",
    prepares: isProductReadinessEnabled() ? radioGroups.value.prepares || "" : "",
  });
}

function handleClose() {
  emit("input", false);
  emit("close");
}

function handleKeydown(event) {
  if (event.key === "Escape" && props.value) handleClose();
}

onMounted(() => {
  document.addEventListener("keydown", handleKeydown);
});

onUnmounted(() => {
  document.removeEventListener("keydown", handleKeydown);
});
</script>

<style scoped>
.filter-employee-modal-enter-active,
.filter-employee-modal-leave-active {
  transition: opacity 180ms ease;
}

.filter-employee-modal-enter-from,
.filter-employee-modal-leave-to {
  opacity: 0;
}

.filter-employee-modal-enter-active .filter-employee-modal__card,
.filter-employee-modal-leave-active .filter-employee-modal__card {
  transition:
    transform 200ms cubic-bezier(0.22, 1, 0.36, 1),
    opacity 180ms ease;
}

.filter-employee-modal-enter-from .filter-employee-modal__card,
.filter-employee-modal-leave-to .filter-employee-modal__card {
  transform: translateY(8px) scale(0.98);
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .filter-employee-modal-enter-active,
  .filter-employee-modal-leave-active,
  .filter-employee-modal-enter-active .filter-employee-modal__card,
  .filter-employee-modal-leave-active .filter-employee-modal__card {
    transition: none !important;
  }
}

.filter-employee-modal__overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(16, 24, 40, 0.5);
  font-family: Inter, sans-serif;
}

.filter-employee-modal__card {
  width: 100%;
  max-width: 1032px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 24px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow:
    0 8px 8px -4px rgba(16, 24, 40, 0.03),
    0 20px 24px -4px rgba(16, 24, 40, 0.08);
  box-sizing: border-box;
}

.filter-employee-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.filter-employee-modal__title {
  color: #101828;
  font-size: 18px;
  font-weight: 600;
  line-height: normal;
}

.filter-employee-modal__close-btn {
  appearance: none;
  border: 0;
  background: transparent;
  padding: 4px;
  margin: -4px;
  cursor: pointer;
  color: #98a2b3;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.filter-employee-modal__close-btn:hover {
  background: rgba(152, 162, 179, 0.12);
}

.filter-employee-modal__close-btn:focus-visible {
  outline: 2px solid #98a2b3;
  outline-offset: 2px;
}

.filter-employee-modal__filters-row {
  display: flex;
  gap: 32px;
  align-items: flex-start;
  width: 100%;
}

.filter-employee-modal__filters-row--secondary > .filter-employee-modal__column {
  flex: 0 1 calc((100% - 96px) / 4);
}

.filter-employee-modal__column {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filter-employee-modal__column-title {
  padding-left: 8px;
  color: #344054;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}

.filter-employee-modal__options {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-left: 8px;
}

.filter-employee-modal__divider {
  height: 1px;
  width: 100%;
  background: #e5e7eb;
}

.filter-employee-modal__department {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.filter-employee-modal__hint {
  margin: 0;
  color: #98a2b3;
  font-size: 12px;
  font-weight: 400;
  line-height: normal;
}

.filter-employee-modal__scope-toggle {
  display: flex;
  width: 100%;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  overflow: hidden;
}

.filter-employee-modal__scope-option {
  flex: 1 1 0;
  min-width: 0;
  appearance: none;
  border: 0;
  background: #fff;
  padding: 10px 16px;
  color: #344054;
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  line-height: normal;
  cursor: pointer;
  text-align: center;
}

.filter-employee-modal__scope-option--active {
  background: #f9f5ff;
  color: #6941c6;
}

.filter-employee-modal__scope-option:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: -2px;
}

.filter-employee-modal__scope-divider {
  width: 1px;
  align-self: stretch;
  background: #d0d5dd;
}

.filter-employee-modal__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.filter-employee-modal__reset {
  appearance: none;
  border: 0;
  background: transparent;
  padding: 4px;
  margin: -4px;
  color: #475467;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  line-height: normal;
  cursor: pointer;
  border-radius: 6px;
}

.filter-employee-modal__reset:hover {
  color: #344054;
  background: rgba(152, 162, 179, 0.08);
}

.filter-employee-modal__reset:focus-visible {
  outline: 2px solid #98a2b3;
  outline-offset: 2px;
}
</style>
